import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Pizza as PizzaIcon,
  Info,
  Check,
  AlertCircle,
  Beef,
  Leaf,
} from "lucide-react";
import { ORDER } from "@/lib/testIds";
import { RESTAURANT, PIZZA } from "@/data/catalog";
import { AddToCartButton } from "@/components/AddToCartButton";
import { HowToOrderBlock } from "@/components/HowToOrderBlock";
import { Seo } from "@/lib/seo";

/* -------------------------------------------------------------------------- */
/*  Calcul du prix estimé (règles métier centralisées)                         */
/* -------------------------------------------------------------------------- */

const SUPP = { viande: 1.5, legume: 0.8, fromage: 1.0, saumon: 2.0 };

const computeTotal = ({ meats, vegs, extraCheeses }) => {
  const basePrice = PIZZA.sizes[0].numeric;
  const meatsCount = meats.length;
  const vegsCount = vegs.length;

  // Composition incluse : jusqu'à 3 ingrédients, max 2 viandes incluses
  const meatsIncluded = Math.min(meatsCount, 2);
  const vegSlotsAvailable = 3 - meatsIncluded;
  const vegsIncluded = Math.min(vegsCount, vegSlotsAvailable);

  const meatsExtra = meatsCount - meatsIncluded; // ex : 3e viande
  const vegsExtra = vegsCount - vegsIncluded;
  const cheesesExtra = extraCheeses.length;
  const salmonSelected = meats.includes("Saumon");

  const extras = {
    meat: meatsExtra * SUPP.viande,
    veg: vegsExtra * SUPP.legume,
    cheese: cheesesExtra * SUPP.fromage,
    salmon: salmonSelected ? SUPP.saumon : 0,
  };
  const extrasTotal =
    extras.meat + extras.veg + extras.cheese + extras.salmon;
  const total = basePrice + extrasTotal;

  return {
    basePrice,
    extras,
    extrasTotal,
    total,
    meatsExtra,
    vegsExtra,
    cheesesExtra,
    salmonSelected,
  };
};

const fmt = (n) => `${n.toFixed(2).replace(".", ",")} €`;

/* -------------------------------------------------------------------------- */
/*  Composants UI                                                              */
/* -------------------------------------------------------------------------- */

const SectionHeading = ({ step, title, required, error }) => (
  <div className="mb-3 flex items-baseline gap-3">
    <span className="font-display text-[#FF9500] text-2xl leading-none">
      {step}
    </span>
    <div className="flex-1">
      <h3 className="font-street text-lg sm:text-xl tracking-wide leading-none">
        {title}
      </h3>
      {required && !error && (
        <div className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-white/40 font-street">
          Choix obligatoire
        </div>
      )}
      {error && (
        <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[#DC2626] font-medium">
          <AlertCircle size={11} />
          {error}
        </div>
      )}
    </div>
  </div>
);

const RadioCard = ({ label, checked, onChange, testId }) => (
  <label
    className={`cursor-pointer rounded-lg border p-3 text-center transition-colors ${
      checked
        ? "border-[#FF9500] bg-[#FF9500]/10"
        : "border-[#262626] bg-[#0F0F0F] hover:border-[#FF9500]/40"
    }`}
  >
    <input
      type="radio"
      className="sr-only"
      checked={checked}
      onChange={onChange}
      data-testid={testId}
    />
    <span className="text-sm text-white leading-tight">{label}</span>
  </label>
);

const CheckCard = ({ label, surcharge, checked, onChange, testId }) => (
  <label
    className={`cursor-pointer flex items-center justify-between gap-2 rounded-lg border px-3 py-3 transition-colors ${
      checked
        ? "border-[#FF9500] bg-[#FF9500]/10"
        : "border-[#262626] bg-[#0F0F0F] hover:border-[#FF9500]/40"
    }`}
  >
    <span className="flex items-center gap-2">
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded border ${
          checked
            ? "bg-[#FF9500] border-[#FF9500] text-black"
            : "border-[#3a3a3a] bg-[#0A0A0A]"
        }`}
      >
        {checked && <Check size={13} strokeWidth={3} />}
      </span>
      <span className="text-sm text-white">{label}</span>
    </span>
    {surcharge && (
      <span className="font-display text-sm text-[#FF9500] whitespace-nowrap">
        {surcharge}
      </span>
    )}
    <input
      type="checkbox"
      className="sr-only"
      checked={checked}
      onChange={onChange}
      data-testid={testId}
    />
  </label>
);

/* -------------------------------------------------------------------------- */
/*  Page Commander                                                             */
/* -------------------------------------------------------------------------- */

const Commander = () => {
  const [base, setBase] = useState(null);
  const [cheese, setCheese] = useState(null);
  const [meats, setMeats] = useState([]);
  const [vegs, setVegs] = useState([]);
  const [extraCheeses, setExtraCheeses] = useState([]);
  const [touched, setTouched] = useState(false);

  const toggle = (arr, setArr, item) => {
    setArr(arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item]);
  };

  const calc = useMemo(
    () => computeTotal({ meats, vegs, extraCheeses }),
    [meats, vegs, extraCheeses]
  );

  const baseError = touched && !base ? "Choisissez une base." : null;
  const cheeseError =
    touched && !cheese ? "Choisissez un fromage inclus." : null;

  const isValid = Boolean(base && cheese);

  const buildPizzaCartItem = () => {
    const details = [
      `Taille : Senior`,
      `Base : ${base}`,
      `Fromage inclus : ${cheese}`,
    ];
    if (meats.length > 0) details.push(`Viandes : ${meats.join(", ")}`);
    if (vegs.length > 0) details.push(`Légumes : ${vegs.join(", ")}`);
    if (extraCheeses.length > 0)
      details.push(`Fromages suppl. : ${extraCheeses.join(", ")}`);
    return {
      kind: "pizza",
      name: "Pizza Senior personnalisée",
      details,
      unitPrice: calc.total,
    };
  };

  return (
    <div
      data-testid={ORDER.root}
      className="mx-auto max-w-6xl px-4 sm:px-6 pt-10 sm:pt-14 pb-32 bg-[#0A0A0A] text-white"
    >
      <Seo
        title="Commander LE 11 - Configurer sa pizza avant appel"
        description="Configurez votre pizza Senior LE 11 (base, fromage, viandes halal, légumes), estimez le prix et préparez votre commande avant d'appeler le restaurant à Villiers-le-Bel."
      />
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.28em] text-[#FF9500] font-street">
          Estimez votre pizza
        </div>
        <h1 className="font-street mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-wide leading-[1.02]">
          Configurez votre <span className="text-[#FF9500]">pizza</span>
        </h1>
        <p className="mt-4 text-sm sm:text-base text-[#A1A1AA] leading-relaxed">
          Composez votre pizza, vérifiez le prix estimé, puis appelez{" "}
          {RESTAURANT.name} pour passer commande.
        </p>
        <p className="mt-2 text-[11px] italic text-white/45">
          Prix estimatif. Commande à confirmer par téléphone.
        </p>

        {/* Bandeau : mention "à composer sur demande" + CTA vers signatures */}
        <div
          data-testid="commander-signatures-banner"
          className="mt-5 flex flex-wrap items-center gap-3 rounded-lg border border-[#C9A875]/30 bg-[#C9A875]/[0.05] px-4 py-3"
        >
          <span className="text-[12.5px] text-white/80 italic">
            Pizza à composer possible sur demande en boutique.
          </span>
          <a
            href="/menu#pizza"
            data-testid="commander-go-signatures"
            className="font-street inline-flex items-center gap-1.5 rounded-md border border-[#F5EFE6]/20 bg-[#F5EFE6]/[0.04] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#F5EFE6] hover:border-[#F5EFE6]/40 hover:bg-[#F5EFE6]/[0.08] transition-colors"
          >
            Voir les pizzas signatures à 10 €
          </a>
        </div>
      </div>

      {/* Bloc "À emporter ou livraison ?" */}
      <HowToOrderBlock variant="commander" />

      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        {/* --- Configurateur --- */}
        <div className="lg:col-span-8 space-y-7">
          {/* Étape 1 — Taille */}
          <section>
            <SectionHeading step="1" title="Taille" />
            <div className="rounded-lg border border-[#FF9500]/40 bg-[#FF9500]/5 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#FF9500] text-black">
                  <PizzaIcon size={16} strokeWidth={2.4} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFC447] font-street">
                    Pizza
                  </div>
                  <div className="font-street text-lg tracking-wider uppercase text-white mt-0.5">
                    Senior
                  </div>
                </div>
              </div>
              <div className="font-display text-3xl leading-none text-[#FF9500]">
                {PIZZA.sizes[0].price}
              </div>
            </div>
          </section>

          {/* Étape 2 — Base */}
          <section>
            <SectionHeading step="2" title="Base" required error={baseError} />
            <div className="grid grid-cols-3 gap-2">
              {PIZZA.bases.map((b) => (
                <RadioCard
                  key={b}
                  label={b}
                  checked={base === b}
                  onChange={() => setBase(b)}
                  testId={`config-base-${b}`}
                />
              ))}
            </div>
          </section>

          {/* Étape 3 — Fromage inclus */}
          <section>
            <SectionHeading
              step="3"
              title="Fromage inclus (1 au choix)"
              required
              error={cheeseError}
            />
            <div className="grid grid-cols-3 gap-2">
              {PIZZA.cheesesIncluded.map((f) => (
                <RadioCard
                  key={f}
                  label={f}
                  checked={cheese === f}
                  onChange={() => setCheese(f)}
                  testId={`config-cheese-${f}`}
                />
              ))}
            </div>
          </section>

          {/* Étape 4 — Viandes */}
          <section>
            <SectionHeading step="4" title="Viandes" />
            <div className="mb-3 flex items-center gap-3 rounded-xl border border-[#FF9500]/20 bg-gradient-to-r from-[#FF9500]/10 via-[#0F0F0F] to-transparent p-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#FF9500] text-black shrink-0">
                <Beef size={18} strokeWidth={2.4} />
              </span>
              <div className="text-[12px] text-[#D4D4D8] leading-snug">
                Choisissez vos viandes — le saumon ajoute toujours{" "}
                <span className="font-display text-[#FF9500]">+2,00 €</span>.
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {PIZZA.meats.map((m) => {
                const checked = meats.includes(m.name);
                const isSalmon = m.name === "Saumon";
                return (
                  <CheckCard
                    key={m.name}
                    label={m.name}
                    surcharge={isSalmon ? "+2,00 €" : null}
                    checked={checked}
                    onChange={() => toggle(meats, setMeats, m.name)}
                    testId={`config-meat-${m.name}`}
                  />
                );
              })}
            </div>
            {calc.meatsExtra > 0 && (
              <div className="mt-3 inline-flex items-start gap-2 rounded-md border border-[#FFC447]/40 bg-[#FFC447]/10 px-3 py-2 text-[11px] text-[#FFC447]">
                <Info size={12} className="shrink-0 mt-0.5" />
                <span>
                  {calc.meatsExtra === 1
                    ? "La 3e viande est comptée en supplément (+1,50 €)."
                    : `${calc.meatsExtra} viandes supplémentaires comptées (+${(calc.meatsExtra * SUPP.viande).toFixed(2).replace(".", ",")} €).`}
                </span>
              </div>
            )}
          </section>

          {/* Étape 5 — Légumes */}
          <section>
            <SectionHeading step="5" title="Légumes" />
            <div className="mb-3 flex items-center gap-3 rounded-xl border border-[#FF9500]/20 bg-gradient-to-r from-[#FFC447]/10 via-[#0F0F0F] to-transparent p-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#FFC447] text-black shrink-0">
                <Leaf size={18} strokeWidth={2.4} />
              </span>
              <div className="text-[12px] text-[#D4D4D8] leading-snug">
                Au-delà des ingrédients inclus :{" "}
                <span className="font-display text-[#FF9500]">+0,80 €</span> par
                légume.
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {PIZZA.vegetables.map((v) => {
                const checked = vegs.includes(v);
                return (
                  <CheckCard
                    key={v}
                    label={v}
                    checked={checked}
                    onChange={() => toggle(vegs, setVegs, v)}
                    testId={`config-veg-${v}`}
                  />
                );
              })}
            </div>
          </section>

          {/* Étape 6 — Fromages supplémentaires */}
          <section>
            <SectionHeading step="6" title="Fromages supplémentaires" />
            <div className="grid grid-cols-3 gap-2">
              {PIZZA.cheesesIncluded.map((f) => {
                const checked = extraCheeses.includes(f);
                return (
                  <CheckCard
                    key={f}
                    label={f}
                    surcharge="+1,00 €"
                    checked={checked}
                    onChange={() =>
                      toggle(extraCheeses, setExtraCheeses, f)
                    }
                    testId={`config-extra-cheese-${f}`}
                  />
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-white/45">
              Chaque fromage supplémentaire ajoute +1,00 €.
            </p>
          </section>
        </div>

        {/* --- Récapitulatif sticky --- */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 rounded-2xl border border-[#262626] bg-[#121212] p-5 sm:p-6">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF9500] font-street">
              Récapitulatif
            </div>
            <h2 className="font-street mt-2 text-2xl tracking-wide leading-none">
              Votre pizza
            </h2>

            <dl className="mt-5 space-y-2.5 text-sm">
              <Row label="Taille" value="Senior" />
              <Row label="Base" value={base || <Missing />} />
              <Row label="Fromage inclus" value={cheese || <Missing />} />
              <Row
                label="Viandes"
                value={meats.length > 0 ? meats.join(", ") : <None />}
              />
              <Row
                label="Légumes"
                value={vegs.length > 0 ? vegs.join(", ") : <None />}
              />
              <Row
                label="Fromages supplémentaires"
                value={
                  extraCheeses.length > 0 ? extraCheeses.join(", ") : <None />
                }
              />
            </dl>

            {/* Détail prix */}
            <div className="mt-5 rounded-lg border border-[#262626] bg-[#0A0A0A] p-3 text-xs space-y-1.5">
              <PriceRow label="Prix de base" value={fmt(calc.basePrice)} />
              {calc.salmonSelected && (
                <PriceRow label="Saumon" value={`+${fmt(SUPP.saumon)}`} />
              )}
              {calc.meatsExtra > 0 && (
                <PriceRow
                  label={`Viande${calc.meatsExtra > 1 ? "s" : ""} suppl.`}
                  value={`+${fmt(calc.extras.meat)}`}
                />
              )}
              {calc.vegsExtra > 0 && (
                <PriceRow
                  label={`Légume${calc.vegsExtra > 1 ? "s" : ""} suppl.`}
                  value={`+${fmt(calc.extras.veg)}`}
                />
              )}
              {calc.cheesesExtra > 0 && (
                <PriceRow
                  label={`Fromage${calc.cheesesExtra > 1 ? "s" : ""} suppl.`}
                  value={`+${fmt(calc.extras.cheese)}`}
                />
              )}
              {calc.extrasTotal === 0 && (
                <PriceRow label="Suppléments" value="0,00 €" muted />
              )}
            </div>

            {/* Total */}
            <div
              data-testid="config-total"
              className="mt-4 rounded-xl bg-[#FF9500] text-black p-4 flex items-center justify-between"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold">
                  Total estimé
                </div>
                <div className="font-display text-4xl leading-none mt-1">
                  {fmt(calc.total)}
                </div>
              </div>
            </div>

            <p className="mt-3 text-[11px] italic text-white/55 leading-snug">
              Appelez le restaurant avec ce récapitulatif sous les yeux pour
              finaliser votre commande.
            </p>

            <a
              href={RESTAURANT.phoneTel}
              data-testid="config-call-cta"
              onClick={() => setTouched(true)}
              className="mt-4 w-full font-street inline-flex items-center justify-center gap-2 rounded-md bg-[#FF9500] px-5 py-3.5 text-base tracking-wider text-black shadow-[0_5px_0_#7a4900] hover:-translate-y-0.5 hover:shadow-[0_7px_0_#7a4900] transition-all"
            >
              <Phone size={17} strokeWidth={2.6} />
              Appeler pour commander
            </a>
            <div className="mt-2 text-center text-[11px] text-white/45">
              {RESTAURANT.phoneDisplay}
            </div>

            {/* Ajout au récapitulatif global */}
            <div className="mt-3">
              {isValid ? (
                <AddToCartButton
                  item={buildPizzaCartItem()}
                  testId="config-add-to-recap"
                  size="md"
                  variant="outline"
                  fullWidth
                  label="Ajouter au récapitulatif"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setTouched(true)}
                  data-testid="config-add-to-recap-disabled"
                  className="w-full font-street inline-flex items-center justify-center gap-2 rounded-md border border-[#262626] bg-[#0A0A0A] px-4 py-2.5 text-sm tracking-wider text-white/45 cursor-pointer"
                >
                  Sélectionnez base et fromage pour ajouter
                </button>
              )}
            </div>

            <Link
              to="/menu"
              className="mt-3 w-full font-street inline-flex items-center justify-center gap-2 rounded-md border border-[#FF9500]/40 px-4 py-2.5 text-xs tracking-wider text-white/80 hover:bg-[#FF9500]/10 transition-colors"
            >
              Voir toute la carte
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-[#1F1F1F] pb-2 last:border-0 last:pb-0">
    <dt className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-street shrink-0">
      {label}
    </dt>
    <dd className="text-right text-white/90 leading-snug">{value}</dd>
  </div>
);

const PriceRow = ({ label, value, muted }) => (
  <div className="flex items-center justify-between">
    <span className={muted ? "text-white/45" : "text-white/75"}>{label}</span>
    <span className={muted ? "text-white/45" : "font-display text-[#FF9500]"}>
      {value}
    </span>
  </div>
);

const Missing = () => (
  <span className="text-[#FF9500] italic text-xs">à choisir</span>
);
const None = () => <span className="text-white/40 italic text-xs">aucun</span>;

export default Commander;
